import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Role } from '@doit/shared';
import { useAuth } from './api/auth';
import { AppLayout } from './layouts/AppLayout';
import { FullPageLoader } from './components/ui';
import { LoginPage } from './pages/auth/LoginPage';
import { InvitePage } from './pages/auth/InvitePage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProfilePage } from './pages/ProfilePage';
import { NotFoundPage } from './pages/PlaceholderPage';
import { CatalogPage } from './pages/catalog/CatalogPage';
import { ImportPage } from './pages/imports/ImportPage';
import { GroupsPage } from './pages/groups/GroupsPage';
import { GroupDetailPage } from './pages/groups/GroupDetailPage';
import { StudentsPage } from './pages/students/StudentsPage';
import { StudentCardPage } from './pages/students/StudentCardPage';
import { SchedulePage } from './pages/schedule/SchedulePage';
import { JournalPage } from './pages/journal/JournalPage';
import { SessionJournalPage } from './pages/journal/SessionJournalPage';
import { BoardPage } from './pages/dashboard/BoardPage';
import { AnalyticsPage } from './pages/dashboard/AnalyticsPage';
import { FlowResultsPage } from './pages/dashboard/FlowResultsPage';
import { DashboardFiltersProvider } from './components/DashboardFilters';
import { UsersPage } from './pages/admin/UsersPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { AuditPage } from './pages/admin/AuditPage';
import { InteractionsPage } from './pages/admin/InteractionsPage';

/** ავტორიზაციის მოთხოვნა + როლური შეზღუდვა */
function Protected({ roles, children }: { roles?: Role[]; children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;

  return <>{children}</>;
}

const STAFF: Role[] = ['ADMIN', 'MANAGER'];

export function App() {
  return (
    <Routes>
      {/* --- ღია მარშრუტები --- */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

      {/* --- დაცული --- */}
      <Route
        element={
          <Protected>
            <DashboardFiltersProvider>
              <AppLayout />
            </DashboardFiltersProvider>
          </Protected>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="/profile" element={<ProfilePage />} />

        <Route path="/groups" element={<GroupsPage />} />
        <Route path="/groups/:id" element={<GroupDetailPage />} />
        <Route path="/schedule" element={<SchedulePage />} />

        <Route
          path="/students"
          element={
            <Protected roles={STAFF}>
              <StudentsPage />
            </Protected>
          }
        />
        <Route path="/students/:id" element={<StudentCardPage />} />
        <Route
          path="/attendance"
          element={
            <Protected roles={STAFF}>
              <BoardPage />
            </Protected>
          }
        />
        <Route
          path="/analytics"
          element={
            <Protected roles={STAFF}>
              <AnalyticsPage />
            </Protected>
          }
        />
        <Route
          path="/flow-results"
          element={
            <Protected roles={STAFF}>
              <FlowResultsPage />
            </Protected>
          }
        />
        <Route
          path="/catalog"
          element={
            <Protected roles={STAFF}>
              <CatalogPage />
            </Protected>
          }
        />
        <Route
          path="/import"
          element={
            <Protected roles={STAFF}>
              <ImportPage />
            </Protected>
          }
        />
        <Route
          path="/users"
          element={
            <Protected roles={STAFF}>
              <UsersPage />
            </Protected>
          }
        />
        <Route path="/journal" element={<JournalPage />} />
        <Route path="/journal/:sessionId" element={<SessionJournalPage />} />
        <Route
          path="/settings"
          element={
            <Protected roles={['ADMIN']}>
              <SettingsPage />
            </Protected>
          }
        />
        <Route
          path="/audit"
          element={
            <Protected roles={['ADMIN']}>
              <AuditPage />
            </Protected>
          }
        />
        <Route
          path="/interactions"
          element={
            <Protected roles={['ADMIN']}>
              <InteractionsPage />
            </Protected>
          }
        />
        {/* ძველი მისამართი — შენახული ბმულები არ უნდა გატყდეს */}
        <Route path="/email-flows" element={<Navigate to="/interactions" replace />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
